import PropTypes from 'prop-types'
import { memo } from 'react'
import { useAtomValue } from 'jotai'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getTimestampByCueIdAtom } from '../../../store/timestamps.store.js'
import { getPreviousCueAtom, getCueAtom } from '../../../store/rundown.store'
import { useTimingInfo } from './useTimingInfo.js'
import TimingFixMenu from './TimingFixMenu.jsx'

function TimingIndicator({ cueId, timezone = 'UTC', buildFixes, readonly = false }) {
  const getTimestampByCueId = useAtomValue(getTimestampByCueIdAtom)
  const getPreviousCueById = useAtomValue(getPreviousCueAtom)
  const getCue = useAtomValue(getCueAtom)

  const cue = getCue(cueId)
  const timestamp = getTimestampByCueId(cueId)
  const previousCueTimestamp = getTimestampByCueId(
    getPreviousCueById(cueId)?.id,
  )

  const timingInfo = useTimingInfo(
    cue,
    previousCueTimestamp,
    timestamp,
    timezone,
  )

  if (!timestamp || !previousCueTimestamp) return null

  const { type, text, textColor, bgColor, icon, differenceMs } = timingInfo

  // A gap/overlap is the only fixable state (type is set only then). The
  // descriptors are NOT built here: `buildFixes` plans every strategy, which
  // runs createTimestamps plus one planBacktime per cue above the anchor. Doing
  // that in the render body repeats it on every tick for every gap cue. Pass
  // the thunk down instead, so TimingFixMenu calls it when the menu opens.
  const isFixable = (type === 'Gap' || type === 'Overlap') && !readonly && !!buildFixes

  return (
    <div className="flex items-center gap-2 leading-5 text-xs text-gray-500">
      <span className={[textColor, 'relative flex h-3 w-3'].join(' ')}>
        <span
          className={[
            'animate-ping absolute inline-flex h-full w-full rounded-full opacity-50',
            bgColor,
          ].join(' ')}
        ></span>
        {icon && <FontAwesomeIcon icon={icon} />}
      </span>
      <span>{text}</span>
      {isFixable ? (
        <TimingFixMenu
          type={type}
          buildFixes={() => buildFixes({ differenceMs, type })}
        />
      ) : null}
    </div>
  )
}

TimingIndicator.propTypes = {
  cueId: PropTypes.string.isRequired,
  timezone: PropTypes.string,
  // Called with { differenceMs, type } when a gap/overlap exists; returns the
  // fix-descriptor list for TimingFixMenu. Omit to render the indicator alone.
  buildFixes: PropTypes.func,
  readonly: PropTypes.bool,
}

export default memo(TimingIndicator)
